import { useRef, useState, useCallback, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import {
  useTranslation,
  MAX_IMAGE_BYTES,
  ACCEPTED_IMAGE_MIME,
  ACCEPTED_IMAGE_MIME_LIST,
} from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';

// Single source of truth lives in `@dukanti/shared/lib/image-validation` so
// F-002's single-file ImageUpload and F-003's MultiImageUpload can never
// drift. Previously F-002 duplicated these as `MAX_FILE_SIZE` /
// `ACCEPTED_TYPES` / `ACCEPT_STRING`; F4d-3 removed that duplication.

export interface ImageUploadProps {
  /** Current file (controlled). null = no file selected. */
  value: File | null;
  /** Called when the user selects or removes a file. */
  onChange: (file: File | null) => void;
  /** Validation error key from parent (optional). */
  error?: string;
  /** data-testid prefix for stable selectors. */
  testIdPrefix?: string;
}

export function ImageUpload({
  value,
  onChange,
  error,
  testIdPrefix = 'image-upload',
}: ImageUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Create/revoke object URL for preview
  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalError(null);
      const file = e.target.files?.[0];
      if (!file) return;

      // Client-side validation (AC3: max 2MB, JPEG/PNG/WEBP)
      if (!ACCEPTED_IMAGE_MIME.has(file.type)) {
        setLocalError('admin.createStore.errors.logoInvalidType');
        // Reset input so the same file can be re-selected after fixing
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setLocalError('admin.createStore.errors.logoTooLarge');
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      onChange(file);
    },
    [onChange],
  );

  const handleRemove = useCallback(() => {
    onChange(null);
    setLocalError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, [onChange]);

  const displayError = localError ?? error;

  return (
    <div data-testid={testIdPrefix}>
      {preview ? (
        <div className="relative inline-block" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={preview}
            alt={t('admin.createStore.logoLabel')}
            className="border-border ring-admin-lime/30 h-24 w-24 rounded-xl border object-cover shadow-sm ring-2"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="bg-foreground text-background hover:bg-foreground/90 focus-visible:ring-ring absolute -end-2 -top-2 inline-flex size-7 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2"
            aria-label={t('admin.createStore.logoRemove')}
            data-testid={`${testIdPrefix}-remove`}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="border-input bg-card text-muted-foreground hover:border-admin-lime hover:bg-admin-surface-muted focus-visible:ring-ring flex h-24 w-24 flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <Upload size={20} aria-hidden />
          <span className="mt-1 text-xs">{t('admin.createStore.logoUpload')}</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_MIME_LIST}
        onChange={handleFileChange}
        className="sr-only"
        data-testid={`${testIdPrefix}-input`}
        tabIndex={-1}
      />

      {displayError ? (
        <p
          role="alert"
          className="text-destructive mt-1 text-start text-sm"
          data-testid={`${testIdPrefix}-error`}
        >
          {t(displayError as TranslationKey)}
        </p>
      ) : null}
    </div>
  );
}
